import Enumerable from "linq";
import { 
  ExpressionKinds,
  IConditionalExpressionBehavior,
  ExpressionReferenceKinds,
  ExpressionOperatorKinds,
  ExpressionLogicalOperatorKinds,
  IExpressionContentBrackets,
  IConditionalExpressionObjectReference,
  IConditionalExpressionPresentation,
  DateDurationKinds,
  ILinkedObjectSpecifiable,
} from "../../../../../../../documaker/domain/format/objects";
import { RelationTranslator } from "./RelationTranslator";
import { IntegerTranslator, MapTranslator } from "../../values";
import { IXElementContentsObjectBean, IXElementContentsObjectRelationalItemBean, IXElementContentsObjectRelationalBean } from "../../../beans";

export class CalculationConditionalExpressionBehaviorTranslator {
  constructor() {
    this.relationalItems = new RelationTranslator("calculate");
  }

  from(bean: IXElementContentsObjectBean): IConditionalExpressionBehavior {
    return {
      contents: this.relationalItems.from(
        bean,
        is => (Enumerable.from(is).select(i => {
          const logicalOperator = this.logicalOperator.from(i["@condition"]);
          return {
            reference: this.objectReference.from(i),
            operator: this.integer.from(i["@operator"]),
            value: "",
            brackets: this.brackets.from(i),
            logicalOperator,
            presentation: this.presentation.from(i),
          };
        }).toArray()),
        []),
    };
  }

  to(object: IConditionalExpressionBehavior): IXElementContentsObjectRelationalBean {
    return this.relationalItems.to(
      Enumerable.from(object.contents)
        .select(s => {
          const result = this.objectReference.to(s.reference, s.operator);
          const presentation = this.presentation.to(s.presentation);
          result["@value-type"] = presentation.viewType;
          result["@value"] = presentation.viewText;
          result["@vobj-type"] = presentation.viewobjType;
          result["@vobj-key"] = presentation.viewobjKey;
          result["@vobj-subkey"] = presentation.viewobjSubkey;
          result["@vobj-option"] = presentation.viewobjOption;
          result["@vobj-id"] = presentation.viewobjId;
          result["@operator"] = this.integer.to(s.operator);
          const brackets = this.brackets.to(s.brackets);
          result["@priority-front"] = brackets.priorityFront;
          result["@priority-back"] = brackets.priorityBack;
          result["@condition"] = this.logicalOperator.to(s.logicalOperator);
          return result;
        })
        .toArray());
  }

  private readonly relationalItems: RelationTranslator;

  private readonly logicalOperator = new MapTranslator<ExpressionLogicalOperatorKinds, string>([
    { object: ExpressionLogicalOperatorKinds.And, bean: "1" },
    { object: ExpressionLogicalOperatorKinds.Or,  bean: "2" },
  ], ExpressionLogicalOperatorKinds.None, undefined);

  private readonly objectReference = new ExpressionObjectReferenceTranslator();  

  private readonly integer = new IntegerTranslator();

  private readonly brackets = new ExpressionBracketsTranslator();

  private readonly presentation = {
    from: (bean: IXElementContentsObjectRelationalItemBean): IConditionalExpressionPresentation => {
      const kind = this.presentaionKind.from(bean["@value-type"]);
      return {
        kind,
        reference: (kind === ExpressionReferenceKinds.Object)
          ? this.objectPresentation.from(bean)
          : bean["@value"],
      };
    },
    to: (object: IConditionalExpressionPresentation) => {
      const result: any = (object.kind === ExpressionReferenceKinds.Object)
        ? this.objectPresentation.to(object.reference as ILinkedObjectSpecifiable)
        : {
          viewText: object.reference as string,
        };
      result.viewType = this.presentaionKind.to(object.kind);
      return result;
    },
  };

  private readonly presentaionKind = new MapTranslator<ExpressionReferenceKinds, string>([
    { object: ExpressionReferenceKinds.Object, bean: "3" },
    // 2:日付は実際には使われてない模様.
    { object: ExpressionReferenceKinds.Fixed,  bean: "1" },
  ], undefined, undefined);

  private readonly objectPresentation = new ExpressionObjectPresentationTranslator();
}

class ExpressionObjectReferenceTranslator {
  from(bean: IXElementContentsObjectRelationalItemBean): ILinkedObjectSpecifiable {
    return {
      type: bean["@type"],
      id: this.integer.from(bean["@obj-id"]),
      key: bean["@key"],
      subKey: bean["@subkey"],
    };
  }

  to(object: ILinkedObjectSpecifiable, operator: ExpressionOperatorKinds): IXElementContentsObjectRelationalItemBean {
    return {
      "@item-type": "1",
      "@type": object.type,
      "@key": object.key,
      "@subkey": object.subKey,
      "@option": "2",
      "@obj-id": this.integer.to(object.id),
      "@operator": this.integer.to(operator),
    };
  }

  private readonly integer = new IntegerTranslator();
}

class ExpressionObjectPresentationTranslator {
  from(bean: IXElementContentsObjectRelationalItemBean): IConditionalExpressionObjectReference {
    const isCalculate = (bean["@value-type"] === "calculate");
    return {
      type: bean["@vobj-type"],
      id: this.integer.from(bean["@vobj-id"]),
      key: bean["@vobj-key"],
      subKey: bean["@vobj-subkey"],
      expressionKind: isCalculate ? this.expressionKind.from(bean["@vobj-option"]) : undefined,
      dateDurationKind: isCalculate ? this.dateDurationKind.from(bean["@vobj-option"]) : undefined,
    };
  }

  to(object: IConditionalExpressionObjectReference) {
    return {
      viewType: "1",
      viewobjType: object.type,
      viewobjKey: object.key,
      viewobjSubkey: object.subKey,
      viewobjId: this.integer.to(object.id),
      viewobjOption: (object.type === "calculate") ? this.expressionKind.to(object.expressionKind, object.dateDurationKind) : undefined,
    };
  }

  private readonly integer = new IntegerTranslator();
  private readonly expressionKind = {
    from: (bean: string): ExpressionKinds => {
      switch (bean) {
        case "1":
          return ExpressionKinds.Calculation;
        case "3":
        case "6":
        case "7":
          return ExpressionKinds.DateDuration;
        default:
          return undefined;
      }
    },
    to: (expressionKind: ExpressionKinds, dateDurationKind: DateDurationKinds): string => {
      switch (expressionKind) {
        case ExpressionKinds.Calculation:
          return "1";
        case ExpressionKinds.DateDuration:
          return this.dateDurationKind.to(dateDurationKind);
        default:
          return undefined;
      }
    },
  };
  private readonly dateDurationKind = new MapTranslator<DateDurationKinds, string>([
    { object: DateDurationKinds.Days,  bean: "3" },
    { object: DateDurationKinds.Weeks, bean: "4" },
    { object: DateDurationKinds.Years, bean: "6" },
    { object: DateDurationKinds.Age,   bean: "7" },
  ], undefined, undefined);
}

class ExpressionBracketsTranslator {
  from(bean: IXElementContentsObjectRelationalItemBean): IExpressionContentBrackets {
    return {
      openCount: this.openCount.from(bean["@priority-front"]),
      closeCount: this.closeCount.from(bean["@priority-back"]),
    };
  }

  to(object: IExpressionContentBrackets) {
    return {
      priorityFront: this.openCount.to(object.openCount),
      priorityBack: this.closeCount.to(object.closeCount),
    };
  }

  private readonly openCount = new MapTranslator<number, string>([
    { object: 1,  bean: "("   },
    { object: 2,  bean: "(("  },
    { object: 3,  bean: "(((" },
  ], 0, undefined);
  private readonly closeCount = new MapTranslator<number, string>([
    { object: 1,  bean: ")"   },
    { object: 2,  bean: "))"  },
    { object: 3,  bean: ")))" },
  ], 0, undefined);
}
